import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import SolicitacaoAcessoModal from '../components/common/SolicitacaoAcessoModal';
import EsqueciSenhaModal from '../components/common/EsqueciSenhaModal';
import RedefinirSenhaModal from '../components/common/RedefinirSenhaModal';
import SelecaoFilialModal from '../components/common/SelecaoFilialModal';
import StatusFlow from '../components/common/StatusFlow';
import WhatsAppWidget from '../components/WhatsAppWidget';

// ============ SLIDES DO PAINEL LATERAL (estilo carrossel institucional) ============
interface Slide {
  titulo: string;
  descricao: string;
  badges: string[];
  visual: React.ReactNode;
}

// Miniatura fiel do calendário mensal de cargas (mesma regra de cores da tela Início)
const MiniCalendarioCargas: React.FC = () => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
  const totalDias = new Date(ano, mes + 1, 0).getDate();
  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const celulas: (number | null)[] = [];
  for (let i = 0; i < primeiroDiaSemana; i++) celulas.push(null);
  for (let d = 1; d <= totalDias; d++) celulas.push(d);

  // Dias fechados de exemplo: primeiro dia útil ~1 e ~2 semanas à frente
  const proximoDiaUtil = (aPartirDe: number): number | null => {
    for (let d = aPartirDe; d <= totalDias; d++) {
      const ds = new Date(ano, mes, d).getDay();
      if (ds !== 0 && ds !== 6) return d;
    }
    return null;
  };
  const diaFechado = proximoDiaUtil(hoje.getDate() + 7);
  const diaFeriado = proximoDiaUtil(hoje.getDate() + 14);

  const corDia = (dia: number): string => {
    const data = new Date(ano, mes, dia);
    const ds = data.getDay();
    const hojeZerado = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    if (data.getTime() === hojeZerado.getTime()) return 'border-2 border-yellow-400 bg-green-500 text-white';
    if (ds === 0 || ds === 6) return 'bg-red-500 text-white font-bold';
    if (data < hojeZerado) return 'bg-gray-100 text-gray-400';
    if (dia === diaFechado) return 'bg-red-500 text-white';
    if (dia === diaFeriado) return 'bg-yellow-500 text-white font-bold';
    return 'bg-green-500 text-white';
  };

  return (
    <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-2xl text-gray-900">
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-400 text-sm">‹</span>
        <div className="text-center">
          <div className="text-sm font-bold">AGENDA UP</div>
          <div className="text-[10px] text-gray-500">{meses[mes]} {ano} • Seg a Sex: 08:00 às 18:00</div>
        </div>
        <span className="text-gray-400 text-sm">›</span>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center mt-2">
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
          <div key={i} className="text-[9px] font-semibold text-gray-500 py-0.5">{d}</div>
        ))}
        {celulas.map((dia, i) => (
          <div key={i} className="aspect-square flex flex-col items-center justify-center">
            {dia !== null && (
              <div className={`w-full h-full rounded-md flex flex-col items-center justify-center text-[10px] font-medium ${corDia(dia)}`}>
                {dia}
                {dia === diaFechado && <span className="text-[7px] font-bold leading-none">Fechado</span>}
                {dia === diaFeriado && <span className="text-[8px] leading-none">🔒</span>}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2.5 pt-2 border-t border-gray-100 flex justify-center">
        <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 rounded-full px-3 py-1">+ Solicitar Agendamento</span>
      </div>
    </div>
  );
};

// Miniatura fiel da grade semanal de visitas (mesmas cores/pills da tela Agendar Visitas)
const MiniAgendaVisitas: React.FC = () => {
  const hoje = new Date();
  const inicioSemana = new Date(hoje);
  inicioSemana.setDate(hoje.getDate() - ((hoje.getDay() + 6) % 7)); // segunda-feira

  const dias = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex'].map((nome, i) => {
    const d = new Date(inicioSemana);
    d.setDate(inicioSemana.getDate() + i);
    return {
      nome,
      data: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
      isHoje: d.toDateString() === hoje.toDateString()
    };
  });

  const horarios = ['08:00', '09:00', '10:00', '14:00', '15:00'];
  // Slots ocupados de exemplo: índice do dia -> horário -> { empresa } ou 'pendente'
  const ocupados: Record<string, string> = {
    '0-09:00': 'Fornecedor Lima',
    '1-10:00': 'PENDENTE',
    '2-08:00': 'Metalúrgica Central',
    '2-14:00': 'PENDENTE',
    '3-15:00': 'Distribuidora Sul',
    '4-08:00': 'Fornecedor Lima'
  };

  return (
    <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-2xl text-gray-900 overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-bold">Agenda de Visitas</div>
          <div className="text-[10px] text-gray-500">Comprador: João Compras</div>
        </div>
        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 rounded-lg px-2 py-1">Semana atual</span>
      </div>
      <table className="w-full table-fixed">
        <thead>
          <tr>
            <th className="w-10"></th>
            {dias.map(d => (
              <th key={d.nome} className={`py-1 text-center text-[10px] font-semibold ${d.isHoje ? 'text-yellow-600' : 'text-gray-700'}`}>
                <div>{d.nome}</div>
                <div className={`font-normal ${d.isHoje ? 'text-yellow-500' : 'text-gray-400'}`}>{d.data}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {horarios.map(h => (
            <tr key={h}>
              <td className="py-1 pr-1 text-[9px] text-gray-500 font-medium text-right">{h}</td>
              {dias.map((d, di) => {
                const ocupado = ocupados[`${di}-${h}`];
                return (
                  <td key={d.nome} className="py-1 px-0.5 text-center">
                    <span className={`flex w-full items-center justify-center gap-1 px-1 py-1 rounded-full text-[9px] font-medium overflow-hidden whitespace-nowrap ${
                      ocupado === 'PENDENTE'
                        ? 'bg-yellow-100 text-yellow-800'
                        : ocupado
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ocupado === 'PENDENTE' ? 'bg-yellow-500' : ocupado ? 'bg-red-500' : 'bg-green-500'}`} />
                      <span className="min-w-0 overflow-hidden text-ellipsis">{ocupado === 'PENDENTE' ? 'Em análise' : ocupado || 'Livre'}</span>
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ====== Miniaturas das telas reais do sistema ======

const MiniCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-2xl text-gray-900 overflow-hidden">
    {children}
  </div>
);

const MiniCardTitulo: React.FC<{ titulo: string; sub?: string }> = ({ titulo, sub }) => (
  <div className="mb-2.5">
    <div className="text-sm font-bold">{titulo}</div>
    {sub && <div className="text-[10px] text-gray-500">{sub}</div>}
  </div>
);

const MiniSolicitacoes: React.FC = () => (
  <MiniCard>
    <MiniCardTitulo titulo="Minhas Solicitações de Agendamento" sub="Acompanhe cada etapa em tempo real" />
    <div className="space-y-2">
      {[
        { cod: 'AGD-1042', forn: 'Fornecedor Lima', data: '15/10 • 09:00', st: 'APROVADO', cor: 'bg-green-100 text-green-700 border-green-300' },
        { cod: 'AGD-1043', forn: 'Metalúrgica Central', data: '16/10 • 14:00', st: 'PENDENTE', cor: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
        { cod: 'AGD-1044', forn: 'Distribuidora Sul', data: '17/10 • 08:00', st: 'REAGENDADO', cor: 'bg-orange-100 text-orange-700 border-orange-300' }
      ].map(s => (
        <div key={s.cod} className="flex items-center gap-3 border border-gray-100 rounded-lg px-3 py-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">{s.forn}</div>
            <div className="text-[10px] text-gray-500">{s.cod} • {s.data}</div>
          </div>
          <span className={`text-[9px] font-bold px-2 py-1 rounded-full border ${s.cor}`}>{s.st}</span>
        </div>
      ))}
    </div>
  </MiniCard>
);

const MiniCheckin: React.FC = () => (
  <MiniCard>
    <MiniCardTitulo titulo="Gestão de Visitas: Check-in" sub="Portaria • Hoje" />
    <div className="space-y-2">
      {[
        { ini: 'FL', nome: 'Fornecedor Lima', quem: 'c/ João Compras', st: 'Em atendimento', cor: 'bg-indigo-500' },
        { ini: 'MC', nome: 'Metalúrgica Central', quem: 'c/ Ana Vendas', st: 'Chegou', cor: 'bg-emerald-500' },
        { ini: 'DS', nome: 'Distribuidora Sul', quem: 'c/ João Compras', st: 'Agendado', cor: 'bg-blue-500' }
      ].map(v => (
        <div key={v.ini} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{v.ini}</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">{v.nome}</div>
            <div className="text-[10px] text-gray-500 truncate">{v.quem}</div>
          </div>
          <span className={`text-[9px] font-semibold px-2 py-1 rounded-full text-white ${v.cor}`}>{v.st}</span>
        </div>
      ))}
    </div>
  </MiniCard>
);

// Réplica da tela real de Cargas: KPIs + tabela com coluna Ordem (N° grande azul + hora)
const MiniOrdemChegada: React.FC = () => (
  <MiniCard>
    <MiniCardTitulo titulo="Cargas do Dia" sub="Gestão de recebimento" />
    <div className="grid grid-cols-3 gap-1.5 mb-2.5">
      {[
        { l: 'Cargas do Dia', v: '7', bg: 'bg-blue-50', tc: 'text-blue-700', vc: 'text-blue-900' },
        { l: 'Finalizadas', v: '3', bg: 'bg-purple-50', tc: 'text-purple-700', vc: 'text-purple-900' },
        { l: 'Ocorrências', v: '1', bg: 'bg-red-50', tc: 'text-red-700', vc: 'text-red-900' }
      ].map(k => (
        <div key={k.l} className={`${k.bg} rounded-lg px-2 py-1.5`}>
          <div className={`text-[8px] font-medium ${k.tc}`}>{k.l}</div>
          <div className={`text-base font-bold ${k.vc}`}>{k.v}</div>
        </div>
      ))}
    </div>
    <table className="w-full table-fixed">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-1.5 py-1.5 text-left text-[8px] font-medium text-gray-500 uppercase">Status</th>
          <th className="px-1.5 py-1.5 text-center text-[8px] font-medium text-gray-500 uppercase w-12">Ordem</th>
          <th className="px-1.5 py-1.5 text-left text-[8px] font-medium text-gray-500 uppercase w-14">Prioridade</th>
          <th className="px-1.5 py-1.5 text-left text-[8px] font-medium text-gray-500 uppercase">Fornecedor</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {[
          { st: 'Descarregando', stc: 'bg-orange-100 text-orange-800', ordem: '1°', hora: '07:42', prio: 'Alta', pc: 'bg-red-100 text-red-800', forn: 'Fornecedor Lima' },
          { st: 'Aguardando', stc: 'bg-yellow-100 text-yellow-800', ordem: '2°', hora: '08:15', prio: 'Média', pc: 'bg-yellow-100 text-yellow-800', forn: 'Metalúrgica Central' },
          { st: 'Finalizada', stc: 'bg-green-100 text-green-800', ordem: '3°', hora: '08:50', prio: 'Baixa', pc: 'bg-green-100 text-green-800', forn: 'Distribuidora Sul' },
          { st: 'Aguardando', stc: 'bg-yellow-100 text-yellow-800', ordem: '', hora: '', prio: 'Média', pc: 'bg-yellow-100 text-yellow-800', forn: 'Transportadora Silva' }
        ].map(c => (
          <tr key={c.forn}>
            <td className="px-1.5 py-1.5"><span className={`inline-flex px-1.5 py-0.5 text-[8px] font-semibold rounded-full ${c.stc}`}>{c.st}</span></td>
            <td className="px-1.5 py-1.5 text-center">
              {c.ordem ? (
                <div className="flex flex-col items-center">
                  <span className="font-bold text-sm text-blue-600 leading-none">{c.ordem}</span>
                  <span className="text-[8px] text-gray-500">{c.hora}</span>
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </td>
            <td className="px-1.5 py-1.5"><span className={`inline-flex px-1.5 py-0.5 text-[8px] font-bold rounded ${c.pc}`}>{c.prio}</span></td>
            <td className="px-1.5 py-1.5 text-[9px] text-gray-900 truncate">{c.forn}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </MiniCard>
);

// Réplica do PDF real gerado por comprovanteService.gerarComprovanteAgendamento
const MiniComprovante: React.FC = () => (
  <div className="w-full max-w-md rounded-xl bg-white shadow-2xl text-gray-900 overflow-hidden">
    {/* Header navy + faixa azul + linha verde (igual ao PDF) */}
    <div className="bg-[#0f172a] px-4 py-2.5 flex items-center gap-3">
      <img src="/Falcon Hub 2.png" alt="Falcon Hub" className="h-6 w-auto" />
      <div className="flex-1 text-center text-[11px] font-bold text-white tracking-wide">
        COMPROVANTE DE AGENDAMENTO
      </div>
      <img src="/Filial 1- UP DISTRIBUIDORA.png" alt="UP" className="h-6 w-auto" />
    </div>
    <div className="h-1 bg-blue-500" />
    <div className="h-[3px] bg-emerald-500" />

    <div className="p-4">
      {/* Box verde: código / status / data */}
      <div className="bg-emerald-500 rounded-lg px-3 py-2 text-[10px] text-black">
        <div className="flex gap-6">
          <span><strong>CÓDIGO</strong> AGD-1042</span>
          <span><strong>STATUS</strong> APROVADO</span>
        </div>
        <div className="mt-1"><strong>DATA:</strong> 15/10/2026</div>
      </div>

      {/* Dados da solicitação (2 colunas) */}
      <div className="mt-3">
        <div className="text-[11px] font-bold text-blue-500">DADOS DA SOLICITAÇÃO</div>
        <div className="h-px bg-[#0f172a] mt-1 mb-1.5" />
        <div className="grid grid-cols-2 gap-x-4 text-[10px]">
          <div className="space-y-1">
            <div><strong>Fornecedor:</strong> Fornecedor Lima</div>
            <div><strong>Produto:</strong> Copos descartáveis</div>
            <div><strong>Peso:</strong> 1.250 kg</div>
            <div><strong>Volume:</strong> 8 caixas</div>
            <div><strong>Tipo Carga:</strong> Secos</div>
          </div>
          <div className="space-y-1">
            <div><strong>Filial:</strong> FILIAL_1</div>
            <div><strong>Solicitante:</strong> Fornecedor Lima</div>
            <div><strong>Data Solicitação:</strong> 12/10/2026</div>
            <div><strong>Ordem Chegada:</strong> #3</div>
            <div><strong>Horário:</strong> 08:47</div>
          </div>
        </div>
      </div>

      {/* Horários */}
      <div className="mt-3">
        <div className="text-[11px] font-bold text-blue-500">HORÁRIO DE FUNCIONAMENTO</div>
        <div className="h-px bg-[#0f172a] mt-1 mb-1.5" />
        <div className="text-[9px] space-y-0.5">
          <div><strong>• Horário de Atendimento:</strong> 08:00 às 18:00</div>
          <div><strong>• Horário de Recebimento de NF:</strong> 08:00 às 17:00</div>
        </div>
      </div>

      {/* Rodapé com QR + texto */}
      <div className="mt-3 pt-2 border-t-2 border-emerald-500 flex items-center gap-3">
        <div className="w-10 h-10 border border-gray-300 rounded grid grid-cols-4 grid-rows-4 gap-px p-0.5 flex-shrink-0">
          {[1,1,0,1,1,0,1,0,0,1,1,1,1,0,0,1].map((v,i) => (
            <div key={i} className={v ? 'bg-black' : 'bg-white'} />
          ))}
        </div>
        <div className="flex-1 text-center">
          <div className="text-[9px]">Comprovante oficial de agendamento - Conservar para apresentação no dia.</div>
          <div className="text-[9px] font-bold text-[#0f172a]">FALCON HUB</div>
          <div className="text-[8px] text-gray-500">Sistema de Gestão de Agendamento</div>
        </div>
      </div>
    </div>
  </div>
);

const MiniEmailNotificacao: React.FC = () => (
  <MiniCard>
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-slate-700 text-white px-3 py-2">
        <div className="text-[10px] font-bold">Falcon Hub • Notificação</div>
      </div>
      <div className="p-3">
        <div className="text-xs font-bold mb-1">Status do seu agendamento mudou</div>
        <div className="text-[10px] text-gray-600 leading-relaxed">
          Olá! Sua solicitação <strong>AGD-1042</strong> foi
          <span className="text-emerald-600 font-bold"> APROVADA</span> para 15/10 às 09:00.
          Apresente o comprovante na portaria.
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[9px] font-semibold text-white bg-blue-600 rounded px-2 py-1">Ver comprovante</span>
          <span className="text-[9px] text-gray-400">para fornecedor@empresa.com</span>
        </div>
      </div>
    </div>
  </MiniCard>
);

const MiniPedidos: React.FC = () => (
  <MiniCard>
    <MiniCardTitulo titulo="Pedidos de Compra" sub="Vínculo com o agendamento" />
    <table className="w-full text-[10px]">
      <thead>
        <tr className="text-gray-500 text-left border-b border-gray-100">
          <th className="pb-1 font-semibold">Pedido</th>
          <th className="pb-1 font-semibold">Fornecedor</th>
          <th className="pb-1 font-semibold text-right">Status</th>
        </tr>
      </thead>
      <tbody>
        {[
          ['PC-88210', 'Fornecedor Lima', 'Digitado', 'text-emerald-600'],
          ['PC-88214', 'Metalúrgica Central', 'Aguardando', 'text-yellow-600'],
          ['PC-88220', 'Distribuidora Sul', 'Recebido', 'text-blue-600']
        ].map(([ped, forn, st, cor]) => (
          <tr key={ped} className="border-b border-gray-50">
            <td className="py-1.5 font-semibold">{ped}</td>
            <td className="py-1.5 text-gray-600">{forn}</td>
            <td className={`py-1.5 text-right font-semibold ${cor}`}>{st}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </MiniCard>
);

const MiniFiliais: React.FC = () => (
  <MiniCard>
    <MiniCardTitulo titulo="Selecione a Filial" sub="Um sistema, múltiplas unidades" />
    <div className="space-y-2">
      {[
        { nome: 'UP Distribuidora', cidade: 'Matriz', ativa: true },
        { nome: 'Aleplast', cidade: 'Filial 2', ativa: false },
        { nome: 'Alepack', cidade: 'Filial 3', ativa: false }
      ].map(f => (
        <div key={f.nome} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${f.ativa ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold ${f.ativa ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
            {f.nome.slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold">{f.nome}</div>
            <div className="text-[10px] text-gray-500">{f.cidade}</div>
          </div>
          {f.ativa && <span className="text-[9px] font-bold text-blue-600">ATIVA</span>}
        </div>
      ))}
    </div>
  </MiniCard>
);

const MiniIndicadores: React.FC = () => (
  <MiniCard>
    <MiniCardTitulo titulo="Dashboard: Indicadores" sub="Visão geral da operação" />
    <div className="grid grid-cols-3 gap-2 mb-3">
      {[{ v: '128', l: 'Cargas/mês', c: 'text-emerald-600' }, { v: '94%', l: 'No horário', c: 'text-blue-600' }, { v: '42', l: 'Visitas', c: 'text-indigo-600' }].map(k => (
        <div key={k.l} className="bg-gray-50 rounded-lg p-2.5 text-center">
          <div className={`text-base font-bold ${k.c}`}>{k.v}</div>
          <div className="text-[9px] text-gray-500 font-medium">{k.l}</div>
        </div>
      ))}
    </div>
    <div className="flex items-end gap-1 h-14 px-1">
      {[40, 65, 50, 80, 55, 95, 70, 60, 85, 75, 90, 100].map((h, i) => (
        <div key={i} className="flex-1 rounded-t bg-emerald-500/80" style={{ height: `${h}%` }} />
      ))}
    </div>
    <div className="text-center text-[9px] text-gray-400 mt-1">Agendamentos por dia</div>
  </MiniCard>
);

const slides: Slide[] = [
  {
    titulo: 'Agendamento de cargas, sua gestão simplificada.',
    descricao: 'Calendário mensal por filial com dias disponíveis, fechamentos e feriados. O fornecedor solicita a entrega direto pela agenda.',
    badges: ['Calendário por filial', 'Fechamento de dias', 'Solicitação online'],
    visual: <MiniCalendarioCargas />
  },
  {
    titulo: 'Agenda de visitas do comprador em tempo real.',
    descricao: 'Grade semanal com horários livres, visitas em análise e fornecedores agendados. Tudo visível na mesma tela.',
    badges: ['Grade semanal', 'Status em tempo real', 'Aprovação pelo comprador'],
    visual: <MiniAgendaVisitas />
  },
  {
    titulo: 'Cada etapa do agendamento, sempre visível.',
    descricao: 'Do pedido de agenda até a finalização na doca. O fluxo mostra exatamente em que ponto cada solicitação está.',
    badges: ['Fluxo de status', 'Progresso visual', 'Do agendamento à doca'],
    visual: (
      <MiniCard>
        <MiniCardTitulo titulo="Etapas do Agendamento" sub="Solicitação AGD-1042 • Fornecedor Lima" />
        <div className="pt-2 scale-[0.95] origin-top">
          <StatusFlow currentStatus="Aprovado" />
        </div>
      </MiniCard>
    )
  },
  {
    titulo: 'Solicitações acompanhadas passo a passo.',
    descricao: 'O fornecedor vê o status de cada agendamento (pendente, aprovado, reagendado) sem precisar ligar ou mandar mensagem.',
    badges: ['Status em tempo real', 'Sem ligações', 'Histórico completo'],
    visual: <MiniSolicitacoes />
  },
  {
    titulo: 'Check-in de visitantes na portaria.',
    descricao: 'Recepção ágil: quem chegou, quem está em atendimento e quem está agendado, organizado em um só painel.',
    badges: ['Check-in digital', 'Controle de acesso', 'Painel da portaria'],
    visual: <MiniCheckin />
  },
  {
    titulo: 'Ordem de chegada nas docas.',
    descricao: 'Fila de descarregamento organizada por ordem real de chegada, com horário registrado automaticamente.',
    badges: ['Fila por doca', 'Registro automático', 'Prioridade transparente'],
    visual: <MiniOrdemChegada />
  },
  {
    titulo: 'Comprovante digital do agendamento.',
    descricao: 'Ao aprovar, o sistema gera o comprovante com código, data, horário e doca. Pronto para apresentar na portaria.',
    badges: ['Comprovante automático', 'Código único', 'Horários da unidade'],
    visual: <MiniComprovante />
  },
  {
    titulo: 'Notificações por e-mail automáticas.',
    descricao: 'A cada mudança de status, o fornecedor recebe um e-mail com todos os detalhes do agendamento.',
    badges: ['E-mail automático', 'Aprovação e reagendamento', 'Sem retrabalho'],
    visual: <MiniEmailNotificacao />
  },
  {
    titulo: 'Pedidos de compra vinculados.',
    descricao: 'Cada agendamento de carga pode ser vinculado ao pedido de compra: rastreabilidade do pedido à doca.',
    badges: ['Vínculo pedido × carga', 'Rastreabilidade', 'Recebimento integrado'],
    visual: <MiniPedidos />
  },
  {
    titulo: 'Um sistema, múltiplas filiais.',
    descricao: 'Agendas independentes por unidade (UP Distribuidora, Aleplast e Alepack) no mesmo painel.',
    badges: ['Multi-filial', 'Agendas independentes', 'Mesmo acesso'],
    visual: <MiniFiliais />
  },
  {
    titulo: 'Indicadores para decidir melhor.',
    descricao: 'Dashboards com volume de cargas, pontualidade e visitas. Dados reais da operação sempre à mão.',
    badges: ['Dashboard', 'Pontualidade', 'Volume por dia'],
    visual: <MiniIndicadores />
  }
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setFilialAtualSemValidacao } = useApp();
  const [formData, setFormData] = useState({
    login: '',
    senha: '',
    captcha: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEsqueciSenhaOpen, setIsEsqueciSenhaOpen] = useState(false);
  const [isRedefinirSenhaOpen, setIsRedefinirSenhaOpen] = useState(false);
  const [isSelecaoFilialOpen, setIsSelecaoFilialOpen] = useState(false);
  const [perfilUsuarioLogado, setPerfilUsuarioLogado] = useState<string>('');
  const [usuarioParaRedefinir, setUsuarioParaRedefinir] = useState<{ id: string; nome: string; filiais: string[] } | null>(null);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaUrl, setCaptchaUrl] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando usuário digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.login) {
      newErrors.login = 'Usuário ou Login é obrigatório';
    }

    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    }

    if (requiresCaptcha && !formData.captcha) {
      newErrors.captcha = 'CAPTCHA é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadCaptcha = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/auth/captcha`, {
        headers: {
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        }
      });
      if (response.ok) {
        const svg = await response.text();
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        setCaptchaUrl(url);
      }
    } catch (error) {
      console.error('Erro ao carregar CAPTCHA:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const user = await login(formData.login, formData.senha, formData.captcha);

      // Verificar se é primeiro acesso
      if (user?.primeiro_acesso) {
        setPerfilUsuarioLogado((user as any)?.perfil || '');
        setUsuarioParaRedefinir({
          id: user.id,
          nome: user.nome,
          filiais: user.filiais || []
        });
        setIsRedefinirSenhaOpen(true);
        setLoading(false);
      } else {
        // Se usuário tem múltiplas filiais, abrir modal de seleção
        if (user?.filiais && user.filiais.length > 1) {
          setPerfilUsuarioLogado((user as any)?.perfil || '');
          setUsuarioParaRedefinir({
            id: user.id,
            nome: user.nome,
            filiais: user.filiais
          });
          setIsSelecaoFilialOpen(true);
          setLoading(false);
        } else {
          // Se tem apenas uma filial, usar ela e navegar
          if (user?.filiais && user.filiais.length > 0) {
            setFilialAtualSemValidacao(user.filiais[0]);
          }
          const perfilLogin = (user as any)?.perfil || '';
          navigate(getRedirectPath(perfilLogin));
        }
      }

    } catch (error: any) {
      console.error('Erro no login:', error);

      // Verificar se o erro indica que CAPTCHA é necessário
      if (error.requiresCaptcha) {
        setRequiresCaptcha(true);
        loadCaptcha();
        setErrors({
          form: error.message || 'Após múltiplas tentativas falhas, é necessário resolver o CAPTCHA.'
        });
      } else {
        setErrors({
          form: error.message || 'Erro ao fazer login. Verifique suas credenciais.'
        });
      }
      setLoading(false);
    }
  };

  const getRedirectPath = (perfil: string): string => {
    if (perfil === 'visitante') return '/agendar-visitas';
    if (perfil === 'fornecedor') return '/inicio';
    const perfisComInicio = ['admin', 'gerente', 'operador', 'fornecedor', 'gestor', 'gestores', 'compras'];
    const perfisAgendarVisitas = ['visitante', 'fornecedor'];
    if (perfisComInicio.includes(perfil)) return '/inicio';
    if (perfisAgendarVisitas.includes(perfil)) return '/agendar-visitas';
    return '/gestao-visitas';
  };

  const handleFilialSelected = (filial: string) => {
    setFilialAtualSemValidacao(filial);
    navigate(getRedirectPath(perfilUsuarioLogado));
  };

  const handleRedefinirSenhaSuccess = () => {
    // Após redefinir senha, seguir para seleção de filial ou primeira aba acessível
    // (o questionário de identificação existe apenas no cadastro "Solicite seu acesso")
    setIsRedefinirSenhaOpen(false);
    const perfilUsuario = perfilUsuarioLogado;
    if (usuarioParaRedefinir && usuarioParaRedefinir.filiais.length > 1) {
      setIsSelecaoFilialOpen(true);
    } else {
      if (usuarioParaRedefinir && usuarioParaRedefinir.filiais.length === 1) {
        setFilialAtualSemValidacao(usuarioParaRedefinir.filiais[0]);
      }
      navigate(getRedirectPath(perfilUsuario));
    }
    setUsuarioParaRedefinir(null);
  };

  // Rotação automática dos slides do painel lateral
  const [slideAtual, setSlideAtual] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setSlideAtual(prev => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides[slideAtual];

  return (
    <div className="min-h-screen flex bg-white">
      {/* ============ PAINEL ESQUERDO: SLIDES INSTITUCIONAIS ============ */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[58%] flex-col bg-[#0f172a] text-white relative overflow-hidden">
        {/* Grid sutil de fundo */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        />
        {/* Glow decorativo */}
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-blue-900/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-blue-800/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          {/* Logo (versão horizontal com fundo transparente) */}
          <div>
            <img src="/Falcon Hub 2.png" alt="Falcon Hub" className="h-12 w-auto" />
          </div>

          {/* Conteúdo do slide */}
          <div key={slideAtual} className="flex-1 flex flex-col justify-center animate-fade-in">
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight max-w-xl">
              {slide.titulo}
            </h1>
            <p className="mt-5 text-base xl:text-lg text-white/70 max-w-lg leading-relaxed">
              {slide.descricao}
            </p>

            {/* Card visual */}
            <div className="mt-10 flex justify-start">
              {slide.visual}
            </div>

            {/* Badges */}
            <div className="mt-6 flex flex-wrap gap-2">
              {slide.badges.map(b => (
                <span key={b} className="px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-medium text-white/80">
                  {b}
                </span>
              ))}
            </div>
          </div>

          {/* Indicadores do carrossel */}
          <div className="flex items-center gap-2 pt-6">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlideAtual(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === slideAtual ? 'w-8 bg-white' : 'w-2 bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ============ PAINEL DIREITO: FORMULÁRIO ============ */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-10 relative">
        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-8">
          <img src="/falcon hub 1.png" alt="Falcon Hub" className="h-16 w-auto mb-2" />
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
              Bem-vindo de volta
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Entre com suas credenciais para acessar o painel.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {errors.form && (
              <div className="rounded-xl bg-red-50 p-4 border border-red-100">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 font-medium">{errors.form}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="relative">
                <Input
                  label="Usuário ou Email"
                  type="text"
                  value={formData.login}
                  onChange={(value) => handleChange('login', value)}
                  error={errors.login}
                  placeholder="Seu usuário ou email"
                  required
                  dataTestId="email"
                />
              </div>

              <div className="relative">
                <Input
                  label="Senha"
                  type="password"
                  value={formData.senha}
                  onChange={(value) => handleChange('senha', value)}
                  error={errors.senha}
                  placeholder="&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;"
                  required
                  dataTestId="password"
                />
                <button
                  type="button"
                  onClick={() => setIsEsqueciSenhaOpen(true)}
                  className="absolute right-0 top-0 text-xs font-semibold text-blue-600 hover:text-blue-500 transition-colors bg-transparent border-none p-0 cursor-pointer"
                  data-testid="esqueci-senha"
                >
                  Esqueceu a senha?
                </button>
              </div>

              {requiresCaptcha && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    CAPTCHA
                  </label>
                  <div className="flex items-center gap-4">
                    {captchaUrl && (
                      <img 
                        src={captchaUrl} 
                        alt="CAPTCHA" 
                        className="border border-gray-300 rounded"
                        onClick={loadCaptcha}
                        style={{ cursor: 'pointer' }}
                        title="Clique para recarregar"
                      />
                    )}
                    <button
                      type="button"
                      onClick={loadCaptcha}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      🔄 Recarregar
                    </button>
                  </div>
                  <Input
                    type="text"
                    value={formData.captcha}
                    onChange={(value) => handleChange('captcha', value)}
                    error={errors.captcha}
                    placeholder="Digite os caracteres do CAPTCHA"
                    required={requiresCaptcha}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
                  Lembrar-me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/politica-privacidade"
                  className="font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                  target="_blank"
                >
                  Política de Privacidade
                </Link>
              </div>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 transform active:scale-[0.98] transition-all font-bold text-base"
                loading={loading}
                disabled={loading}
                dataTestId="login-button"
              >
                {loading ? 'Verificando...' : 'Entrar no Sistema'}
              </Button>
            </div>

            <div className="text-center pt-4">
              <p className="text-sm text-gray-500">
                Novo por aqui?{' '}
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="font-bold text-blue-600 hover:text-blue-500 transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  Solicite seu acesso
                </button>
              </p>
            </div>

            {/* Botão de emergência para limpar bloqueios */}
            <div className="text-center pt-2 border-t border-gray-200 mt-4">
              <button
                type="button"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-none p-0 cursor-pointer"
              >
                🔄 Limpar sessão e tentar novamente
              </button>
            </div>
          </form>

          {/* Versão / footer */}
          <div className="mt-10 text-center space-y-1">
            <p className="text-sm font-bold text-gray-800">Falcon Hub</p>
            <p className="text-xs text-gray-400">Agendamento de cargas inteligente · v4.0.6</p>
            <p className="text-[11px] text-gray-400 pt-2">
              © {new Date().getFullYear()} UP Distribuidora de Produtos e Descartáveis LTDA.
            </p>
          </div>
        </div>
      </div>

      <SolicitacaoAcessoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <EsqueciSenhaModal
        isOpen={isEsqueciSenhaOpen}
        onClose={() => setIsEsqueciSenhaOpen(false)}
      />

      <RedefinirSenhaModal
        isOpen={isRedefinirSenhaOpen}
        onClose={() => {
          setIsRedefinirSenhaOpen(false);
          setUsuarioParaRedefinir(null);
        }}
        usuarioId={usuarioParaRedefinir?.id || ''}
        usuarioNome={usuarioParaRedefinir?.nome || ''}
        onSuccess={handleRedefinirSenhaSuccess}
      />

      <SelecaoFilialModal
        isOpen={isSelecaoFilialOpen}
        onClose={() => setIsSelecaoFilialOpen(false)}
        onFilialSelected={handleFilialSelected}
        filiaisDisponiveis={usuarioParaRedefinir?.filiais || []}
      />

      <WhatsAppWidget />
    </div>
  );
};

export default Login;
